<template>
  <div>
    <h6
      v-if="promotionData === null"
      class="heading-small text-muted mb-4 text-truncate"
    >
      Add New Promotion
    </h6>
    <h6 v-else class="heading-small text-muted mb-4 text-truncate">
      Edit Promotion
    </h6>

    <form id="form-proper">
      <div class="py-2" style="border-bottom: 1px solid #e0e0e0">
        <a-tabs default-active-key="info">
          <a-tab-pane key="info" tab="Promotion Info">
            <div class="row">
              <div class="col">
                <base-input label="Name" required>
                  <a-input v-model="formData.name" :disabled="viewMode" />
                  <div v-if="$v.formData.name.$dirty" class="text-danger">
                    <div class="error" v-if="!$v.formData.name.required">
                      Field is required
                    </div>
                  </div>
                </base-input>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input label="Description">
                  <a-textarea
                    v-model="formData.description"
                    :auto-size="{ minRows: 5, maxRows: 5 }"
                    :disabled="viewMode"
                  />
                </base-input>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input label="Promotion Date" required>
                  <br />
                  <a-range-picker
                    v-model="startEndDateRange"
                    :default-value="[formData.start_date, formData.end_date]"
                    :format="dateFormat"
                    @change="dateRangeOnChange"
                    style="width: 100%"
                    :disabled="viewMode"
                  />
                  <div v-if="$v.formData.start_date.$dirty" class="text-danger">
                    <div class="error" v-if="!$v.formData.start_date.required">
                      Start date is required
                    </div>
                  </div>
                  <div v-if="$v.formData.end_date.$dirty" class="text-danger">
                    <div class="error" v-if="!$v.formData.end_date.required">
                      End date is required
                    </div>
                  </div>
                </base-input>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input>
                  <b-form-checkbox
                    v-model="archivePromoFlag"
                    name="check-button"
                    switch
                    :disabled="viewMode"
                  >
                    <label class="form-control-label">Archive Promo</label>
                  </b-form-checkbox>
                </base-input>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <base-input label="Remarks">
                  <a-textarea
                    v-model="formData.remarks"
                    :auto-size="{ minRows: 5, maxRows: 5 }"
                    :disabled="viewMode"
                  />
                </base-input>
              </div>
            </div>
          </a-tab-pane>

          <a-tab-pane key="content" tab="Promotion Content">
            <div class="row">
              <div class="col">
                <base-input label="Details">
                  <a-textarea
                    v-model="formData.details"
                    :auto-size="{ minRows: 5, maxRows: 5 }"
                    :disabled="viewMode"
                  />
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <base-input label="Upload Image">
                  <template #rightLabelAddon
                    ><small>(700x300px)</small></template
                  >
                  <br />
                  <a-upload
                    :file-list="fileList"
                    :showUploadList="false"
                    :remove="handleRemove"
                    :before-upload="beforeUpload"
                    :disabled="viewMode"
                  >
                    <a-button> <a-icon type="upload" /> Select File </a-button>
                  </a-upload>
                </base-input>
              </div>
            </div>

            <div class="row" v-if="imgPreview">
              <div class="col">
                <base-input label="Image Preview: ">
                  <br />
                  <a-button
                    type="danger"
                    @click="handleRemove"
                    block
                    :disabled="viewMode || formSubmitting"
                  >
                    <i class="fa fa-trash"> </i> Remove Image
                  </a-button>
                  <br />
                  <img :src="imgPreview" />
                </base-input>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
      <div
        style="
          position: absolute;
          bottom: 0px;
          right: 0px;
          width: -webkit-fill-available;
          background-color: #f2f2f2;
          padding: 10px;
        "
      >
        <div class="container mx-0 px-0">
          <div class="row">
            <div class="col-auto mr-auto">
              <b-button
                size="sm"
                class="float-md-left"
                style="margin-left: 8px"
                @click.prevent="closeDrawer()"
              >
                Cancel
              </b-button>
            </div>
            <div class="col-auto">
              <template>
                <a-spin :spinning="formSubmitting">
                  <b-button
                    v-if="promotionData === null"
                    size="sm"
                    variant="primary"
                    @click.prevent="createPromotion"
                  >
                    Create
                  </b-button>

                  <b-button
                    v-else
                    size="sm"
                    variant="primary"
                    @click.prevent="confirmUpdate"
                    :disabled="viewMode"
                  >
                    Update
                  </b-button>
                </a-spin>
              </template>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import HTTPRequest from '../../controllers/httpRequest';
import moment from 'moment';
import axios from 'axios';
import secureStorage from '../../controllers/secureStorage';

import tokenHandler from '../../controllers/token';

const S3Api = process.env.VUE_APP_BXI_S3_API;
const PromotionAPI = process.env.VUE_APP_PROMOTION_API;

export default {
  name: 'PromotionForm',
  props: {
    promotionData: Object,
    viewMode: Boolean,
    drawerVisible: Boolean
  },
  data() {
    return {
      formSubmitting: false,
      maxFileSize: 2, // in MB
      dateFormat: 'YYYY-MM-DD',
      startEndDateRange: null,
      fileList: [],
      imgPreview: null,
      formData: {
        name: null,
        description: null,
        type: 'ADS',
        start_date: null,
        end_date: null,
        details: null,
        img_url: null,
        promo_status: null,
        remarks: null
      }
    };
  },

  validations: {
    formData: {
      name: {
        required
      },
      type: { required },
      start_date: { required },
      end_date: { required }
    }
  },

  computed: {
    archivePromoFlag: {
      get() {
        return this.formData.promo_status === 'Archived' ? true : false;
      },
      set(val) {
        if (val) {
          this.formData.promo_status = 'Archived';
        } else {
          this.formData.promo_status = null;
        }
      }
    },

    accountName() {
      return this.$store.getters.getAccount?.account_name ?? null;
    }
  },

  methods: {
    /**
     * For file uploading methods
     */
    async beforeUpload(file) {
      // Check file type
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        this.$message.error('You can only upload .jpeg or .png file!');
        return false;
      }

      // Check if filesize is > (n)mb
      const fileSize = file.size / 1024 / 1024 < this.maxFileSize;
      if (!fileSize) {
        this.$message.error('Image must smaller than 2MB!');
        return false;
      }

      // Check if already uploaded atleast 1 file
      if (this.fileList.length > 0) {
        this.$message.error('Only one (1) image is allowed to be uploaded!');
        return false;
      }

      const { src } = await this.readFileContent(file);
      this.imgPreview = src;

      this.fileList = [...this.fileList, file];

      return false;
    },

    handleRemove() {
      this.imgPreview = null;
      this.fileList = [];
      this.formData.img_url = null;
    },

    async s3Upload(file = [], firstTry = true) {
      if (file.length > 0) {
        console.log('Uploading to S3');
        const formData = new FormData();
        formData.append('file', file[0]);

        let filePath = null;
        try {
          const upload = await axios({
            method: 'POST',
            url:
              S3Api +
              '?account_name=' +
              this.accountName +
              '&profile=promotion-images',
            headers: {
              Authorization: 'Bearer ' + secureStorage.getItem('uploadToken')
            },
            data: formData
          });

          if (typeof upload.data.path !== 'undefined' && upload.data.path) {
            console.log('File uploaded');
            filePath = upload.data.path;
          } else {
            console.log('No upload path');
            this.$message.error('Unable to upload image. Please try again!');
          }

          return filePath;
        } catch (err) {
          let retries = 3;
          let isReloggedIn = false;

          const responseStatus = err.response.status;

          if (responseStatus === 401 && firstTry) {
            while (!isReloggedIn && retries > 0) {
              try {
                isReloggedIn = await tokenHandler.refreshS3Token();
              } catch (err) {
                console.log('Failed to refresh token');
              } finally {
                retries--;
                this.$message.info('Retrying to upload image.');
              }
            }

            if (isReloggedIn) {
              return await this.s3Upload(this.fileList, false);
            }
          }
          if (firstTry) {
            this.$message.error(
              'Unable to upload image. Something went wrong!'
            );
          }

          throw err;
        }
      }

      return null;
    },

    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (evt) => {
          let img = new Image();
          img.src = evt.target.result;
          img.onload = function () {
            resolve({
              src: img.src,
              width: img.width,
              height: img.height
            });
          };
        };
        reader.onerror = reject;
      });
    },
    /**
     * /for file uploading methods
     */

    async createPromotion() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.formSubmitting = true;

        try {
          const s3Path = await this.s3Upload(this.fileList);

          if (s3Path) {
            this.formData.img_url = s3Path;
          } else {
            this.formData.img_url = null;
          }

          const response = await HTTPRequest.postRequest(
            PromotionAPI + '/promotions',
            {
              promotion: this.formData
            },
            secureStorage.getItem('promotionToken')
          );

          if (response.id) {
            this.closeDrawer();
            this.$root.$emit('refreshTable');
            this.$notification.success({
              message: 'Promotion has been created.'
            });
          } else {
            if (typeof response.error !== 'undefined') {
              this.$notification.error({
                message: response.error
              });
            } else {
              this.$notification.error({
                message: 'Something went wrong. Please try again.'
              });
            }
          }
        } catch (err) {
          console.log('[CATCH]: ', err);
        } finally {
          this.formSubmitting = false;
        }
      } else {
        this.$notification.error({
          message: 'Please fill-up the required(*) fields.',
          placement: 'topLeft'
        });
      }
    },
    async editPromotion() {
      this.$v.formData.$touch();

      if (!this.$v.formData.$invalid) {
        this.formSubmitting = true;

        try {
          if (this.fileList.length) {
            const s3Path = await this.s3Upload(this.fileList);

            if (s3Path) {
              this.formData.img_url = s3Path;
            } else {
              this.formData.img_url = null;
            }
          }

          const response = await HTTPRequest.putRequest(
            PromotionAPI + '/promotions/' + this.promotionData.id,
            {
              promotion: this.formData
            },
            secureStorage.getItem('promotionToken')
          );

          if (response.id) {
            this.closeDrawer();
            this.$root.$emit('refreshTable');
            this.$notification.success({
              message: 'Promotion has been updated.'
            });
          } else {
            if (typeof response.error !== 'undefined') {
              this.$notification.error({
                message: response.error
              });
            } else {
              this.$notification.error({
                message: 'Something went wrong. Please try again.'
              });
            }
          }
        } catch (err) {
          console.log('[CATCH]: ', err);
        } finally {
          this.formSubmitting = false;
        }
      } else {
        this.$notification.error({
          message: 'Please fill-up the required(*) fields.',
          placement: 'topLeft'
        });
      }
    },
    confirmUpdate() {
      this.$confirm({
        title: 'Confirm',
        content: 'Are you sure you want to commit the changes you made?',
        onOk: () => {
          this.editPromotion();
        }
      });
    },

    dateRangeOnChange(dateObj, dateString) {
      this.startEndDateRange = dateString;
      this.formData.start_date = dateString[0] ?? null;
      this.formData.end_date = dateString[1] ?? null;
      console.log('dateObj: ', dateObj);
    },

    closeDrawer() {
      this.clearForm();
      this.$emit('closeDrawer', null);
      this.$v.formData.$reset();
    },
    clearForm() {
      this.formData = {
        name: null,
        description: null,
        type: 'ADS',
        start_date: null,
        end_date: null,
        details: null,
        img_url: null,
        promo_status: null,
        remarks: null
      };
      this.startEndDateRange = null;
      this.fileList = [];
      this.imgPreview = null;
    },
    async setFormData(promotionData) {
      if (promotionData !== null) {
        const _startDate = moment(promotionData.start_date).format(
          this.dateFormat
        );
        const _endDate = moment(promotionData.end_date).format(this.dateFormat);

        promotionData.start_date = _startDate;
        promotionData.end_date = _endDate;

        this.startEndDateRange = [_startDate, _endDate];

        if (
          typeof promotionData.img_url !== 'undefined' &&
          promotionData.img_url
        ) {
          this.imgPreview = promotionData.img_url;
        } else {
          this.imgPreview = null;
        }

        this.formData = { ...promotionData };
      } else {
        this.clearForm();
      }
    }
  },

  created() {
    this.setFormData(this.promotionData);
  },

  watch: {
    promotionData: function (newVal) {
      if (newVal !== null) {
        this.setFormData(newVal);
      } else {
        this.clearForm();
      }
    },

    drawerVisible: function (val) {
      if (val == false) {
        this.closeDrawer();
      }
    }
  }
};
</script>

<style scoped></style>
